import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';
import { validateResourceUrl } from '../utils/validation.js';

export interface GoogleGemini3ProImageParams {
  /**
   * 图像生成提示词，长度必须在1-2500字符之间
   */
  prompt: string;
  /**
   * 输入图像URL列表，最多支持5个图像
   */
  image_urls?: string[];
  /**
   * 图像宽高比，支持: 1:1, 2:3, 3:2, 3:4, 4:3, 4:5, 5:4, 9:16, 16:9, 21:9
   * @values 1:1 | 2:3 | 3:2 | 3:4 | 4:3 | 4:5 | 5:4 | 9:16 | 16:9 | 21:9
   */
  aspect_ratio?: '1:1' | '2:3' | '3:2' | '3:4' | '4:3' | '4:5' | '5:4' | '9:16' | '16:9' | '21:9';
  /**
   * 分辨率，支持: 1K,2K,4K
   * @values 1K | 2K | 4K
   */
  resolution?: '1K' | '2K' | '4K';
}

export interface GoogleGemini3ProImageResource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * google_gemini3_pro_image
 *
 * <p>Google Gemini 图像生成实现。</p>
 * <p>Model: google_gemini3_pro_image<br>
 * Features: 文本生成图像 (Text-to-Image), 图像编辑 (Image Editing), 多图像合成 (Multi-Image Composition)<br>
 * Input Images: 最多支持3张图像<br>
 * Aspect Ratios: 1:1, 2:3, 3:2, 3:4, 4:3, 4:5, 5:4, 9:16, 16:9, 21:9</p>
 * 
 *
 * @example
 * ```typescript
 * import { initSeacloud, googleGemini3ProImage } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await googleGemini3ProImage({
 *   // 参数见 GoogleGemini3ProImageParams 接口定义
 * });
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 GoogleGemini3ProImageParams 接口定义
 * @returns 生成的资源列表
 */
export async function googleGemini3ProImage(
  params: GoogleGemini3ProImageParams,
): Promise<GoogleGemini3ProImageResource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 3000,
    maxAttempts: 60,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'google_gemini3_pro_image',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: GoogleGemini3ProImageResource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          // Validate URL before adding to results
          validateResourceUrl(resource, 'image');

          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
